import React, { useState, useMemo, useEffect, useRef } from 'react'
import { css } from '@emotion/react'
import isInteger from 'lodash/isInteger'
import isNumber from 'lodash/isNumber'

import AddIcon from '../icons/AddIcon'
import MinusIcon from '../icons/MinusIcon'
import Colors from '../theme/Colors'

export default function PositiveIntegerInput(props: {
  'data-testid'?: string
  min?: number
  max?: number
  initialValue: number
  onChange: (value: number) => void
  disabled?: boolean
}) {
  const initialValue = useMemo(() => validateValue(props.initialValue), [props.min, props.max])
  const [inputValue, setInputValue] = useState<string>(String(initialValue))
  const previousValidValue = useRef<number>(initialValue)

  useEffect(
    () => {
      if (props.initialValue !== initialValue) {
        props.onChange(initialValue)
      }
    },
    [initialValue] /* Do not add `props.initialValue` to the dependency array */,
  )

  function validateValue(value: string | number): number {
    const min = props.min ?? -Infinity
    const max = props.max ?? Infinity
    const num = typeof value === 'string' ? parseInt(value) : Math.floor(value)

    if (!isInteger(num)) {
      return initialValue
    }

    if (num < min) {
      return min
    } else if (max && num > max) {
      return max
    } else {
      return num
    }
  }

  const valid = inputValue === String(validateValue(inputValue))
  const underflow = isNumber(props.min) && Number(inputValue) <= props.min
  const overflow = isNumber(props.max) && Number(inputValue) >= props.max

  const onSubmit = (incomingValue: number) => {
    if (isNaN(incomingValue)) {
      setInputValue(String(initialValue))

      if (initialValue !== previousValidValue.current) {
        props.onChange(initialValue)
        previousValidValue.current = initialValue
      }

      return
    }

    const validValue = validateValue(incomingValue)
    setInputValue(String(validValue))

    if (validValue !== previousValidValue.current) {
      props.onChange(validValue)
      previousValidValue.current = validValue
    }
  }

  return (
    <div
      css={css`
        display: inline-block; // Shrink the size
      `}
    >
      <div
        css={css`
          display: flex; // Align children from left to right
        `}
      >
        <AdjustButton
          className="ax-positive-integer-input__decrease-button"
          disabled={props.disabled || underflow || !valid}
          onClick={() => {
            onSubmit(parseInt(inputValue) - 1)
          }}
        >
          <MinusIcon />
        </AdjustButton>

        <input
          data-testid={props['data-testid']}
          css={css`
            width: 50px; // This is required for input[type=number]; See https://stackoverflow.com/questions/22709792/input-type-number-wont-resize
            padding: 12px 10px;
            border: 1px solid ${Colors.Gray100};
            border-right-color: transparent;
            border-left-color: transparent;
            background: ${Colors.White};
            text-align: center;
            box-sizing: border-box;
            color: ${Colors.Gray600};
            outline: none;

            :focus {
              border-width: 1px;
              border-color: ${Colors.Teal400};
            }

            ::-webkit-outer-spin-button,
            ::-webkit-inner-spin-button {
              appearance: none;
            }
          `}
          type="number"
          disabled={props.disabled}
          min={props.min}
          max={props.max}
          value={inputValue}
          step={1}
          pattern="[0-9]"
          onChange={(e) => {
            setInputValue(e.target.value)
            // Do not call `onSubmit` here as users may not finish typing
          }}
          onBlur={(e) => {
            const value = e.target.value.trim()
            onSubmit(parseInt(value))
          }}
        />

        <AdjustButton
          className="ax-positive-integer-input__increase-button"
          disabled={props.disabled || overflow || !valid}
          onClick={() => {
            onSubmit(parseInt(inputValue) + 1)
          }}
        >
          <AddIcon />
        </AdjustButton>
      </div>
    </div>
  )
}

function AdjustButton(props: {
  className?: string
  children: React.ReactElement
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button" // Prevent <form> submission
      className={props.className}
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
        background: ${Colors.White};
        border: 1px solid ${Colors.Gray100};
        color: ${Colors.Gray500};
        min-width: 42px;
        outline: none;

        :first-of-type,
        :first-of-type:disabled {
          padding: 0 1px 0 0;
          border-right-width: 0;
          border-top-left-radius: 2px;
          border-bottom-left-radius: 2px;
        }

        :last-of-type,
        :last-of-type:disabled {
          padding: 0 0 0 1px;
          border-left-width: 0;
          border-top-right-radius: 2px;
          border-bottom-right-radius: 2px;
        }

        :focus {
          padding: 0;
          border: 1px solid ${Colors.Teal400};
        }

        :hover {
          background: ${Colors.Gray50};
        }

        :active {
          background: ${Colors.Gray100};
        }

        :disabled {
          border: 1px solid ${Colors.Gray100};
          color: ${Colors.Gray100};
          background: ${Colors.White};
        }
      `}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  )
}
